/**
 * Stale-chunk recovery for main.tsx (CLEANLIST-PORTAL-63): what a
 * `vite:preloadError` does. The comment in main.tsx says why it reloads.
 */
import { useSyncExternalStore } from 'react';

// The build this tab runs: the commit SHA on deploys (netlify.toml sets
// VITE_RELEASE to it), else `cleanlist-portal@<package version>`
// (__APP_VERSION__, baked in by vite.config.ts). Datadog reports it as the
// version too.
export const BUILD_ID: string =
  import.meta.env.VITE_RELEASE || `cleanlist-portal@${__APP_VERSION__}`;

// Reload-loop guard: a genuinely broken deploy (chunk truly 404s post-refresh)
// would otherwise reload forever. Before reloading we write the running build
// (BUILD_ID) to sessionStorage, and refuse to reload again only while the tab
// still runs that same build: the reload did not move it, so another would
// not either. A flag any other build left never blocks, so a stale build
// always gets its reload to the new deploy, even when an earlier page unloaded
// before main.tsx's clean-load clear ran (CLEANLIST-PORTAL-90: 5 errors in
// 13s, all on the same old release). main.tsx still clears the flag
// after a clean load, so a later, unrelated stale-chunk error on the same build
// can recover again.
export const PRELOAD_RELOAD_FLAG = 'cleanlist:preload-reloaded';
// Only stale-chunk (dynamic-import) misses are recoverable by reloading, and
// browsers word that failure differently, so match each. WebKit (Safari, and
// Chrome on iOS) gets index.html for a missing old-hash chunk from the SPA
// fallback in public/_redirects, and says "'text/html' is not a valid
// JavaScript MIME type" (Sentry CLEANLIST-PORTAL-9E). Other preload failures
// (e.g. a transient network error) wouldn't be fixed by a reload and could
// loop, so we leave them to surface normally. "Loading chunk N failed" is
// webpack's wording: Clerk's own UI bundle (/npm/@clerk/ui/…) uses it when one
// of its lazy chunks misses on Safari/iOS, leaving a blank sign-in page.
const CHUNK_LOAD_ERROR_RE =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|is not a valid JavaScript MIME type|Loading chunk \S+ failed/i;

/** True for an error a reload fixes: a chunk from an older deploy is gone. */
export function isStaleChunkError(message: string | undefined): boolean {
  return CHUNK_LOAD_ERROR_RE.test(message ?? '');
}

// When a reload was last asked for. Until the tab unloads (a deferral can wait
// up to DEFER_CAP_MS), the failed import still rejects into the router's error
// boundaries: RouteError shows nothing for it and telemetry's rumBeforeSend
// drops it, since the reload is already fixing it.
let reloadRequestedAt = Number.NEGATIVE_INFINITY;
const RELOAD_QUIET_MS = 10_000;

/** True from a stale-chunk reload being asked for until the tab should have
 *  unloaded. */
export function reloadPending(): boolean {
  return Date.now() - reloadRequestedAt < RELOAD_QUIET_MS;
}

// Holds: a screen with unsaved work (the play editor) registers a check here.
// While any check returns true, a stale chunk does not reload the tab, since
// the browser would ask "Reload site? Changes may not be saved" and OK throws
// the work away. The screen shows "New version available" instead and reloads
// when the user is ready.
const holds = new Set<() => boolean>();
const listeners = new Set<() => void>();
let updateAvailable = false;

/** Holds stale-chunk reloads while `check()` is true. Returns the release. */
export function holdReload(check: () => boolean): () => void {
  holds.add(check);
  return () => {
    holds.delete(check);
  };
}

export function reloadHeld(): boolean {
  return [...holds].some((check) => check());
}

// Short waits: something that must be written down before the tab reloads
// (a brand-new Clu chat streaming before the server names it) registers a
// check here. Unlike a hold, it does not keep the tab: the reload waits while
// any check is true, polling every DEFER_POLL_MS, and goes ahead regardless
// after DEFER_CAP_MS.
const deferrals = new Set<() => boolean>();
const DEFER_POLL_MS = 100;
const DEFER_CAP_MS = 2000;

/** Delays a stale-chunk reload while `check()` is true, up to a short cap.
 *  Returns the release. */
export function deferReload(check: () => boolean): () => void {
  deferrals.add(check);
  return () => {
    deferrals.delete(check);
  };
}

const reloadDeferred = () => [...deferrals].some((check) => check());

/** Calls `reload` once no deferral asks to wait, or once the cap elapses. */
function reloadWhenClear(reload: () => void) {
  if (!reloadDeferred()) {
    reload();
    return;
  }
  const startedAt = Date.now();
  const timer = setInterval(() => {
    if (reloadDeferred() && Date.now() - startedAt < DEFER_CAP_MS) return;
    clearInterval(timer);
    reload();
  }, DEFER_POLL_MS);
}

function markUpdateAvailable() {
  if (updateAvailable) return;
  updateAvailable = true;
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** True once a stale chunk was seen while a hold kept the tab from reloading. */
export function useUpdateAvailable(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => updateAvailable,
    () => false
  );
}

/** Reloads the page (`reload`) for a stale-chunk preload error `message`,
 *  unless a hold is active: then it marks the update available and returns
 *  'held', with no reload and no loop guard set. A deferral delays the
 *  reload briefly (see `deferReload`). */
export function reloadOnStaleChunk(
  message: string | undefined,
  reload: () => void
): 'reload' | 'held' | 'ignored' {
  if (!isStaleChunkError(message)) return 'ignored';
  // Reload-loop guard: if we already reloaded from this very build and the
  // chunk STILL fails on it, let the error surface (and get reported)
  // instead of looping. sessionStorage can throw a SecurityError in
  // locked-down browsers/iframes, and a storage failure must never abort
  // recovery, so on any throw we fall through and still reload.
  try {
    if (sessionStorage.getItem(PRELOAD_RELOAD_FLAG) === BUILD_ID)
      return 'ignored';
  } catch {
    /* storage unreadable: proceed with the reload */
  }
  // Unsaved work holds the reload. No loop guard is written, so the reload
  // still happens once the hold is released and the next chunk misses.
  if (reloadHeld()) {
    markUpdateAvailable();
    return 'held';
  }
  // No event.preventDefault() in main.tsx: with it, Vite's preload helper
  // resolves the failed import with undefined instead of rejecting, so
  // TanStack's lazy route reads `.component` off undefined and throws ("Page
  // unavailable" flashed before the reload, Sentry CLEANLIST-PORTAL-9B). Left
  // to reject, the router sees a missing module and suspends while we reload.
  try {
    sessionStorage.setItem(PRELOAD_RELOAD_FLAG, BUILD_ID);
  } catch {
    /* storage unwritable: reload anyway (loop guard degrades, recovery wins) */
  }
  reloadRequestedAt = Date.now();
  // At once, with a Clu reply streaming too: the turn goes on on the server
  // and the chat reattaches to it after the reload (P0-118). Waiting for it
  // left the route the user clicked hanging. The one wait is a brand-new
  // chat that has no id yet, for at most DEFER_CAP_MS: without its id the
  // tab cannot reopen it after the reload.
  reloadWhenClear(reload);
  return 'reload';
}
